/**
 * As marcas de tempo do turno, do lado da TELA — diagnóstico DIAG-DELAY-1.
 *
 * POR QUE ISTO EXISTE. O Denis mede ~30 s entre apertar enviar e ver a
 * resposta. Ninguém sabe ONDE esses segundos moram: pode ser rede, fila do
 * motor, montagem de contexto, latência do modelo, proxy segurando o fio ou a
 * própria tela só pintando no fim. Cada palpite custa um deploy. Uma marca em
 * cada fronteira responde de uma vez, com número.
 *
 * ISTO NÃO MUDA COMPORTAMENTO. Só escreve no console do navegador. Nenhum
 * `await`, nenhum estado, nenhum caminho novo — se esta função sumisse, o turno
 * correria idêntico.
 *
 * PREFIXO ESTÁVEL. `[DIAG-DELAY]` para o Denis filtrar o console com uma
 * palavra só, e para o par no motor usar o mesmo prefixo.
 *
 * A CHAVE É O `clientRequestId`. Ele já existe e já viaja no corpo do POST
 * (`connectionStore.ts:1125` gera, `chat-stream.ts` envia), então tela e motor
 * falam do MESMO turno sem inventar identificador novo.
 */

export type PontoDaTela =
  /** (a) o `fetch` do `/api/v1/chat/stream` acabou de sair. */
  | 'a-fetch-disparado'
  /** (b1) primeiro pedaço de BYTES vindo do fio — normalmente o `processing`. */
  | 'b1-primeiro-byte'
  /** (b2) primeiro evento que carrega CONTEÚDO (`chunk`/`envelope`/`response`). */
  | 'b2-primeiro-conteudo'
  /** (c) evento terminal (`response`, `done` ou `error`). */
  | 'c-terminal'

/**
 * Uma marca no console, com relógio de parede e o relógio monotônico.
 *
 * O ISO é o que casa com o log do motor (fusos diferentes, mesma linha do
 * tempo). O `performance.now()` é o que dá o Δ confiável dentro da tela, porque
 * ele não anda para trás quando o relógio do sistema é ajustado.
 */
export function marcaDaTela(
  ponto: PontoDaTela,
  turnId: string | undefined,
  extra?: Record<string, unknown>,
): void {
  console.info('[DIAG-DELAY]', {
    lado: 'tela',
    ponto,
    turnId: turnId ?? null,
    quando: new Date().toISOString(),
    monotonicoMs: Math.round(performance.now()),
    ...extra,
  })
}

/**
 * O estado das marcas de UM stream — uma marca por fronteira, por turno.
 *
 * Mora aqui, e não em variáveis soltas no laço de leitura, por um motivo
 * medido: o `switch` dos eventos já corre a seis blocos de profundidade, e o
 * portão de modularidade reprova qualquer `if` novo ali (limite 4). Um objeto
 * de estado mais uma chamada de uma linha resolve sem afrouxar o portão.
 */
export interface EstadoDasMarcas {
  primeiroConteudo: boolean
}

/** Um estado zerado, um por stream. */
export function estadoInicialDasMarcas(): EstadoDasMarcas {
  return { primeiroConteudo: false }
}

/** Os eventos que provam que a espera acabou: texto, card ou a resposta. */
const EVENTOS_COM_CONTEUDO = new Set(['chunk', 'envelope', 'response'])
/** Os eventos que fecham o turno. */
const EVENTOS_TERMINAIS = new Set(['response', 'done', 'error'])

/**
 * Marca (b2) e (c) a partir do tipo do evento SSE recebido.
 *
 * Chamada uma vez por evento, sem condição do lado de quem chama — toda a
 * decisão é daqui.
 */
export function marcaEventoRecebido(
  type: string,
  turnId: string | undefined,
  estado: EstadoDasMarcas,
): void {
  if (!estado.primeiroConteudo && EVENTOS_COM_CONTEUDO.has(type)) {
    estado.primeiroConteudo = true
    marcaDaTela('b2-primeiro-conteudo', turnId, { evento: type })
  }
  if (EVENTOS_TERMINAIS.has(type)) {
    marcaDaTela('c-terminal', turnId, { evento: type })
  }
}

/**
 * Marcas de performance do clique de onboarding (F1.5 do piloto): clique, aviso
 * local ("pensando" aceso), resposta SSE e o próximo cartão montado. Usam a User
 * Timing API do navegador (`performance.mark/measure`, sem dependência) com o
 * prefixo `axg:onb:`; cada ponto depois do clique ganha um `measure` desde ele.
 *
 * Só marcam depois de um clique de cadastro (`click`) e se encerram no
 * `proximo-cartao`, então um turno comum nunca polui a linha do tempo.
 */
export type PontoOnb = 'click' | 'feedback' | 'resposta' | 'proximo-cartao'

const PREFIXO_ONB = 'axg:onb:'
let cliqueOnbEmAberto = false

export function marcaOnb(ponto: PontoOnb): void {
  if (ponto !== 'click' && !cliqueOnbEmAberto) return
  try {
    performance.mark(`${PREFIXO_ONB}${ponto}`)
    if (ponto === 'click') cliqueOnbEmAberto = true
    else {
      performance.measure(`${PREFIXO_ONB}click->${ponto}`, `${PREFIXO_ONB}click`, `${PREFIXO_ONB}${ponto}`)
      if (ponto === 'proximo-cartao') cliqueOnbEmAberto = false
    }
  } catch {
    // Diagnóstico nunca derruba o turno (ambiente sem User Timing).
  }
}

export function _resetMarcasOnbParaTeste(): void {
  cliqueOnbEmAberto = false
}
